import { createContext, use } from 'react';

/**
 * Context for sharing row metadata with descendant components.
 *
 * This enables field components to access the rowId for registering
 * their popovers with the global table editing store.
 */
export interface RowContextValue {
  /** Unique identifier for this row (the task ID, entity ID, etc.) */
  rowId: string;
}

export const RowContext = createContext<RowContextValue | null>(null);

/**
 * Hook to access the row context.
 * Returns null if used outside a table row (e.g., in a standalone field).
 */
export function useRowContext(): RowContextValue | null {
  return use(RowContext);
}

/**
 * Hook to access the row context, throwing if not available.
 * Use this when the component must be rendered within a table row.
 */
export function useRequiredRowContext(): RowContextValue {
  const context = use(RowContext);
  if (!context) {
    throw new Error('useRequiredRowContext must be used within a DataTable row');
  }
  return context;
}
